import { API } from "@aws-amplify/api";
import { Auth } from "aws-amplify";
import { API_USER_EVENT } from "../../constants";

const eventAPI = API_USER_EVENT;
const eventsPath = "/events";

/**
 * Get current user info (username and email)
 * @returns {Promise<Object>} User info with username and email
 */
async function getCurrentUserInfo() {
  try {
    const user = await Auth.currentAuthenticatedUser();
    return {
      username: user.username || null,
      email: user.attributes?.email || null
    };
  } catch (error) {
    console.error("Failed to get current user info:", error);
    return { username: null, email: null };
  }
}

/**
 * Create a new event
 * @param {Object} eventData - Event data
 * @param {string} eventData.eventType - Type: 'assignment', 'note', 'match', 'group_match'
 * @param {string} eventData.description - Description (required for assignments)
 * @param {string} eventData.status - Assignment status: 'open' or 'closed' (for assignment events)
 * @param {Object} eventData.location - Geographic location data
 * @returns {Promise<Object>} Created event
 */
export async function createEvent(eventData) {
  try {
    // Get current user info and add to event data
    const userInfo = await getCurrentUserInfo();
    const enrichedData = {
      ...eventData,
      cognitoUsername: userInfo.username,
      cognitoEmail: userInfo.email
    };

    const response = await API.post(eventAPI, eventsPath, {
      body: enrichedData
    });
    return response;
  } catch (error) {
    console.error("Create event failed:", error);
    throw error;
  }
}

/**
 * Create an assignment event
 * @param {string} description - One line description
 * @param {Object} location - Geographic location (optional)
 * @param {string} status - Assignment status: 'open' or 'closed' (default: 'open')
 * @returns {Promise<Object>} Created event
 */
export async function createAssignment(description, location = null, status = 'open') {
  return createEvent({
    eventType: 'assignment',
    description,
    status,
    ...(location && { location })
  });
}

/**
 * Toggle assignment status between 'open' and 'closed'
 * @param {string} eventId - Assignment event ID
 * @param {string} newStatus - New status: 'open' or 'closed'
 * @returns {Promise<Object>} Updated event
 */
export async function toggleAssignmentStatus(eventId, newStatus) {
  return updateEvent(eventId, { status: newStatus });
}


/**
 * Create a face match event
 * @param {string} personName - Name of the matched person
 * @param {string} personImage - Image URL of the matched person
 * @param {Object} person - Full person object with additional data
 * @param {Object} openAssignment - Open assignment at time of match (optional, kept for backward compatibility)
 * @param {Object} location - Geographic location (optional)
 * @param {string} matchSource - Source of the match: 'face_match' or 'manual_filter' (default: 'manual_filter')
 * @param {number} matchAccuracy - Match accuracy as 0-1 value (optional, for AI matches)
 * @param {string} note - Additional note for the match (optional)
 * @param {string} capturedImageUrl - URL of captured image used for AI match (optional, for face_match only)
 * @param {Object} boundingBox - Face bounding box from Rekognition (optional, for multi-face detection)
 * @param {number} matchCount - Number of faces matched in image (optional, for multi-face detection)
 * @param {number} detectedFaceCount - Total number of faces detected in image (optional, for multi-face detection)
 * @param {string} groupEventId - Parent group match event ID (optional, for multi-face detection)
 * @param {string} assignmentEventId - Parent assignment event ID (optional)
 * @returns {Promise<Object>} Created event
 */
export async function createMatchEvent(personName, personImage, person = null, openAssignment = null, location = null, matchSource = 'manual_filter', matchAccuracy = null, note = null, capturedImageUrl = null, boundingBox = null, matchCount = null, detectedFaceCount = null, groupEventId = null, assignmentEventId = null) {
  const description = `Matched with ${personName}`;

  return createEvent({
    eventType: 'match',
    description,
    metadata: {
      personName,
      personImage,
      matchSource,
      ...(matchAccuracy !== null && { matchAccuracy }),
      ...(person && { person }),
      ...(openAssignment && { openAssignment }),
      ...(assignmentEventId && { assignmentEventId }),
      ...(note && { note }),
      ...(capturedImageUrl && { capturedImageUrl }),
      ...(boundingBox && { boundingBox }),
      ...(matchCount !== null && { matchCount }),
      ...(detectedFaceCount !== null && { detectedFaceCount }),
      ...(groupEventId && { groupEventId })
    },
    ...(location && { location })
  });
}

/**
 * Create a group match event (parent event for multi-face matches)
 * @param {Object} location - Geographic location (optional)
 * @param {number} matchCount - Number of faces matched
 * @param {number} detectedFaceCount - Total number of faces detected
 * @param {string} capturedImageUrl - URL of captured image
 * @param {Array<string>} personNames - Names of all matched people
 * @param {string} assignmentEventId - Parent assignment event ID (optional)
 * @returns {Promise<Object>} Created group match event
 */
export async function createGroupMatchEvent(location = null, matchCount, detectedFaceCount, capturedImageUrl, personNames = [], assignmentEventId = null) {
  const description = `Group photo: ${matchCount}/${detectedFaceCount} faces matched`;

  return createEvent({
    eventType: 'group_match',
    description,
    metadata: {
      matchCount,
      detectedFaceCount,
      capturedImageUrl,
      personNames,
      matchSource: 'face_match',
      ...(assignmentEventId && { assignmentEventId })
    },
    ...(location && { location })
  });
}

/**
 * Get a specific event by ID
 * @param {string} eventId - Event ID
 * @returns {Promise<Object>} Event data
 */
export async function getEvent(eventId) {
  try {
    const response = await API.get(eventAPI, `/event/${eventId}`);
    return response;
  } catch (error) {
    console.error("Get event failed:", error);
    throw error;
  }
}

/**
 * List events for the current user
 * @param {Object} options - Query options
 * @param {string} options.eventType - Filter by event type
 * @param {number} options.limit - Max number of events to return (default: 20)
 * @param {string} options.nextToken - Token for pagination (from previous response)
 * @returns {Promise<Object>} Events list with { events: Array, nextToken: string|null }
 */
export async function listEvents(options = {}) {
  try {
    const queryParams = {};
    if (options.eventType) queryParams.eventType = options.eventType;
    if (options.status) queryParams.status = options.status;
    if (options.limit) queryParams.limit = options.limit;
    if (options.nextToken) queryParams.nextToken = options.nextToken;

    const response = await API.get(eventAPI, eventsPath, {
      queryStringParameters: queryParams
    });
    return response;
  } catch (error) {
    console.error("List events failed:", error);
    throw error;
  }
}

/**
 * Get all open assignments
 * @returns {Promise<Array>} List of open assignments
 */
export async function getOpenAssignments() {
  try {
    // Query for assignment with status='open'
    // Note: DynamoDB applies filters AFTER retrieving items based on limit
    // So we need a higher limit to ensure we scan enough assignments to find the open one
    const response = await listEvents({
      eventType: 'assignment',
      status: 'open',
      limit: 100
    });

    return response.events || [];
  } catch (error) {
    console.error("Get open assignments failed:", error);
    throw error;
  }
}

/**
 * Update an event
 * @param {string} eventId - Event ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated event
 */
export async function updateEvent(eventId, updates) {
  try {
    const response = await API.put(eventAPI, `/event/${eventId}`, {
      body: updates
    });
    return response;
  } catch (error) {
    console.error("Update event failed:", error);
    throw error;
  }
}

/**
 * Delete an event
 * @param {string} eventId - Event ID
 * @returns {Promise<void>}
 */
export async function deleteEvent(eventId) {
  try {
    await API.del(eventAPI, `/event/${eventId}`);
  } catch (error) {
    console.error("Delete event failed:", error);
    throw error;
  }
}

/**
 * Get current geographic location using user settings
 * @param {Object} settings - User settings from SettingsContext
 * @param {boolean} settings.locationHighAccuracy - Use GPS for better accuracy
 * @param {number} settings.locationAcquisitionTimeout - Timeout in milliseconds
 * @param {number} settings.locationCacheMaxAge - Max age of cached position in milliseconds
 * @returns {Promise<Object|null>} Location object with latitude, longitude, accuracy, timestamp or null if unavailable
 */
export async function getCurrentLocation(settings) {
  if (!settings) {
    console.warn('getCurrentLocation called without settings');
    return null;
  }

  const {
    locationHighAccuracy = true,
    locationAcquisitionTimeout = 10000,
    locationCacheMaxAge = 0
  } = settings;

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation not available');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp).toISOString()
        });
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: locationHighAccuracy,
        timeout: locationAcquisitionTimeout,
        maximumAge: locationCacheMaxAge
      }
    );
  });
}
